import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import {
  ISeedBalanceRow,
  ISeedCellInput,
  ISeedSaveResponse,
  ISeedImportResponse,
} from "./SeedBalances.types";
import { IStoreState } from "../../initialStoreState";
import { showMessage } from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { translateBackendError } from "../../../helpers/errorTranslator";

// ============================================================================
// Grid load
// ============================================================================
export const FETCH_SEED_BALANCES_GRID_PROGRESS =
  "FETCH_SEED_BALANCES_GRID_PROGRESS";
export const FETCH_SEED_BALANCES_GRID_SUCCESS =
  "FETCH_SEED_BALANCES_GRID_SUCCESS";
export const FETCH_SEED_BALANCES_GRID_FAILED =
  "FETCH_SEED_BALANCES_GRID_FAILED";

export const fetchSeedBalancesGridProgress = () =>
  action(FETCH_SEED_BALANCES_GRID_PROGRESS);
export const fetchSeedBalancesGridSuccess = (data: ISeedBalanceRow[]) =>
  action(FETCH_SEED_BALANCES_GRID_SUCCESS, { data });
export const fetchSeedBalancesGridFailed = () =>
  action(FETCH_SEED_BALANCES_GRID_FAILED);

interface IFetchSeedBalancesGridParams {
  fiscal_years_code: string;
  branch_code: string;
}

export const fetchSeedBalancesGridAsync =
  ({
    fiscal_years_code,
    branch_code,
  }: IFetchSeedBalancesGridParams): ThunkAction<
    void,
    IStoreState,
    {},
    AnyAction
  > =>
  async (dispatch) => {
    try {
      dispatch(fetchSeedBalancesGridProgress());

      const finalUrl = `/accounting/seed-balances?fiscal_years_code=${fiscal_years_code}&branch_code=${branch_code}`;

      const res = await api.get(finalUrl);
      const data: ISeedBalanceRow[] = res.data.data;
      dispatch(fetchSeedBalancesGridSuccess(data));
    } catch (err: any) {
      dispatch(fetchSeedBalancesGridFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

// ============================================================================
// Save (PUT)
// ============================================================================
export const SAVE_SEED_BALANCES_PROGRESS = "SAVE_SEED_BALANCES_PROGRESS";
export const SAVE_SEED_BALANCES_SUCCESS = "SAVE_SEED_BALANCES_SUCCESS";
export const SAVE_SEED_BALANCES_FAILED = "SAVE_SEED_BALANCES_FAILED";

export const saveSeedBalancesProgress = () =>
  action(SAVE_SEED_BALANCES_PROGRESS);
export const saveSeedBalancesSuccess = () => action(SAVE_SEED_BALANCES_SUCCESS);
export const saveSeedBalancesFailed = () => action(SAVE_SEED_BALANCES_FAILED);

interface ISaveSeedBalancesParams {
  fiscal_years_code: string;
  branch_code: string;
  adjust_mode: string;
  cells: ISeedCellInput[];
}

export const saveSeedBalancesAsync =
  (
    { fiscal_years_code, branch_code, adjust_mode, cells }: ISaveSeedBalancesParams,
    onCallback?: (isSuccess: boolean, response?: ISeedSaveResponse) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveSeedBalancesProgress());

      const res = await api.put("/accounting/seed-balances", {
        fiscal_years_code,
        branch_code,
        adjust_mode,
        cells,
      });
      const response: ISeedSaveResponse = res.data.data;
      dispatch(saveSeedBalancesSuccess());

      // Surface the friendly error when the server blocks the save even though
      // the request itself succeeded (period closed / RE blocked). The view
      // inspects the response to decide what to show.
      if (response?.period_closed || response?.retained_earnings_blocked) {
        dispatch(
          showMessage({
            type: "error",
            message: translateBackendError(response),
            displayAs: "snackbar",
          }),
        );
      } else {
        dispatch(
          showMessage({
            type: "success",
            message: "Seed balances saved successfully!",
            displayAs: "snackbar",
          }),
        );
      }

      if (onCallback) {
        onCallback(true, response);
      }
    } catch (err: any) {
      dispatch(saveSeedBalancesFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
      if (onCallback) {
        onCallback(false);
      }
    }
  };

// ============================================================================
// History
// ============================================================================
export const FETCH_SEED_BALANCES_HISTORY_PROGRESS =
  "FETCH_SEED_BALANCES_HISTORY_PROGRESS";
export const FETCH_SEED_BALANCES_HISTORY_SUCCESS =
  "FETCH_SEED_BALANCES_HISTORY_SUCCESS";
export const FETCH_SEED_BALANCES_HISTORY_FAILED =
  "FETCH_SEED_BALANCES_HISTORY_FAILED";

export const fetchSeedBalancesHistoryProgress = () =>
  action(FETCH_SEED_BALANCES_HISTORY_PROGRESS);
export const fetchSeedBalancesHistorySuccess = (data: ISeedBalanceRow[]) =>
  action(FETCH_SEED_BALANCES_HISTORY_SUCCESS, { data });
export const fetchSeedBalancesHistoryFailed = () =>
  action(FETCH_SEED_BALANCES_HISTORY_FAILED);

interface IFetchSeedBalancesHistoryParams {
  fiscal_years_code: string;
  branch_code: string;
  gl_account_code?: string;
}

export const fetchSeedBalancesHistoryAsync =
  ({
    fiscal_years_code,
    branch_code,
    gl_account_code,
  }: IFetchSeedBalancesHistoryParams): ThunkAction<
    void,
    IStoreState,
    {},
    AnyAction
  > =>
  async (dispatch) => {
    try {
      dispatch(fetchSeedBalancesHistoryProgress());

      let finalUrl = `/accounting/seed-balances/history?fiscal_years_code=${fiscal_years_code}&branch_code=${branch_code}`;
      if (gl_account_code) {
        finalUrl += `&gl_account_code=${gl_account_code}`;
      }

      const res = await api.get(finalUrl);
      const data: ISeedBalanceRow[] = res.data.data;
      dispatch(fetchSeedBalancesHistorySuccess(data));
    } catch (err: any) {
      dispatch(fetchSeedBalancesHistoryFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

// ============================================================================
// Import (POST JSON — csv_content string)
// ============================================================================
interface IImportSeedBalancesParams {
  csv_content: string;
  fiscal_years_code: string;
  branch_code: string;
  adjust_mode: string;
}

export const importSeedBalancesAsync =
  (
    { csv_content, fiscal_years_code, branch_code, adjust_mode }: IImportSeedBalancesParams,
    onCallback?: (isSuccess: boolean, response?: ISeedImportResponse) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveSeedBalancesProgress());

      const res = await api.post("/accounting/seed-balances/import", {
        csv_content,
        fiscal_years_code,
        branch_code,
        adjust_mode,
      });
      const response: ISeedImportResponse = res.data.data;
      dispatch(saveSeedBalancesSuccess());

      if (response?.posted === false) {
        // The server rejected the CSV without writing anything. Surface the
        // reason; the dialog renders parse_errors[] so the user can fix the
        // file. period_closed / retained_earnings_blocked get the friendly
        // message; otherwise a generic parse-failure note.
        if (response.period_closed || response.retained_earnings_blocked) {
          dispatch(
            showMessage({
              type: "error",
              message: translateBackendError(response),
              displayAs: "snackbar",
            }),
          );
        } else {
          dispatch(
            showMessage({
              type: "error",
              message:
                "The CSV has validation errors and was not imported. Fix the highlighted rows and re-upload.",
              displayAs: "snackbar",
            }),
          );
        }
      } else {
        dispatch(
          showMessage({
            type: "success",
            message: "Seed balances imported successfully!",
            displayAs: "snackbar",
          }),
        );
      }

      if (onCallback) {
        onCallback(true, response);
      }
    } catch (err: any) {
      dispatch(saveSeedBalancesFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
      if (onCallback) {
        onCallback(false);
      }
    }
  };

// ============================================================================
// Export (GET blob -> browser download)
// ============================================================================
interface IExportSeedBalancesParams {
  fiscal_years_code: string;
  branch_code: string;
}

export const exportSeedBalancesAsync =
  ({
    fiscal_years_code,
    branch_code,
  }: IExportSeedBalancesParams): ThunkAction<
    void,
    IStoreState,
    {},
    AnyAction
  > =>
  async (dispatch) => {
    try {
      const finalUrl = `/accounting/seed-balances/export?fiscal_years_code=${fiscal_years_code}&branch_code=${branch_code}`;

      const res = await api.get(finalUrl, {
        responseType: "blob",
      });

      const contentType =
        (res.headers?.["content-type"] as string) || "text/csv";
      const blob =
        res.data instanceof Blob
          ? new Blob([res.data], { type: contentType })
          : new Blob([res.data], { type: contentType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `seed_balances_${fiscal_years_code}_${branch_code}.csv`,
      );
      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        }),
      );
    }
  };

// ============================================================================
// Clear
// ============================================================================
export const CLEAR_SEED_BALANCES_STATE = "CLEAR_SEED_BALANCES_STATE";
export const clearSeedBalancesState = () => action(CLEAR_SEED_BALANCES_STATE);
